import { useCallback, useEffect, useRef, useState } from 'react';
import { useToggle } from '../../../hooks/useToggle';
import { TABLE_SEARCH_DEBOUNCE_MS } from '../constants';
import type {
  TableFilterOptionsLoader,
  TableFilterOptionsPage,
} from '../types';
import { TableFilterOptionsHelper } from '../utils/filter-options';

export function useRemoteFilterOptions(
  loader: TableFilterOptionsLoader | undefined,
  search: string,
  enabled: boolean
) {
  const generation = useRef(0);
  const inFlight = useRef(false);
  const failedRequest = useRef<{ offset: number; append: boolean } | null>(
    null
  );
  const [page, setPage] = useState<TableFilterOptionsPage>({
    options: [],
    nextOffset: null,
  });
  const { value: loading, setValue: setLoading } = useToggle();
  const { value: error, setValue: setError } = useToggle();

  const request = useCallback(
    async (offset: number, append: boolean, current: number) => {
      if (
        !loader ||
        !enabled ||
        inFlight.current ||
        generation.current !== current
      )
        return;
      inFlight.current = true;
      failedRequest.current = null;
      setLoading(true);
      setError(false);
      try {
        const result = await loader(search.trim(), offset);
        if (generation.current !== current) return;
        setPage((previous) => ({
          options: TableFilterOptionsHelper.mergeOptions(
            append ? previous.options : [],
            result.options
          ),
          nextOffset: result.nextOffset,
        }));
      } catch {
        if (generation.current === current) {
          failedRequest.current = { offset, append };
          setError(true);
        }
      } finally {
        if (generation.current === current) {
          inFlight.current = false;
          setLoading(false);
        }
      }
    },
    [loader, search, enabled, setLoading, setError]
  );

  useEffect(() => {
    generation.current += 1;
    const current = generation.current;
    inFlight.current = false;
    failedRequest.current = null;
    setPage({ options: [], nextOffset: null });
    setError(false);
    setLoading(Boolean(loader && enabled));
    if (!loader || !enabled) return undefined;
    const timer = setTimeout(() => {
      request(0, false, current);
    }, TABLE_SEARCH_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      generation.current += 1;
    };
  }, [loader, enabled, request, setLoading, setError]);

  const retry = useCallback(() => {
    const failed = failedRequest.current;
    if (failed) {
      request(failed.offset, failed.append, generation.current);
    }
  }, [request]);

  const loadMore = useCallback(() => {
    if (page.nextOffset !== null) {
      request(page.nextOffset, true, generation.current);
    }
  }, [page.nextOffset, request]);

  return {
    ...page,
    loading,
    error,
    retry,
    loadMore,
  };
}
